/**
 * Modal "Terminar aplicación" (008 · T087 · US5; contracts/admin-api.md A14): `POST
 * /schedule-pattern-assignments/{id}/end { end_day }`. Los días posteriores ya generados quedan
 * "Sin horario" salvo los ajustes manuales, que se conservan. Por defecto termina hoy (zona de la
 * empresa); nunca antes del inicio de la aplicación. Errores con campo al "Último día"; el resto
 * dentro del diálogo.
 */
import { useState } from 'react';

import { EndPatternApplicationBodySchema, type WorkerSchedulePattern } from '@fermin/contracts';

import { Button } from '../../../components/ui/button';
import { Dialog, DialogContent } from '../../../components/ui/dialog';
import { Field } from '../../../components/ui/field';
import { Input } from '../../../components/ui/input';
import { useToast } from '../../../components/ui/toast';
import { t } from '../../../i18n';
import { readOnlyProps, useReadOnly } from '../../shell/use-read-only';
import { isStatusConflict, scheduleErrorMessage, useEndPatternApplication } from '../api';
import { formatLocalDay } from '../format';

export interface EndApplicationDialogProps {
  application: WorkerSchedulePattern | null;
  onClose: () => void;
  today: string;
}

/** Último día por defecto: hoy, o el inicio si la aplicación aún no empezó. */
export function defaultEndDay(
  application: Pick<WorkerSchedulePattern, 'start_day'>,
  today: string,
) {
  return application.start_day > today ? application.start_day : today;
}

/** El contenido se monta por aplicación: cada apertura parte del último día por defecto. */
export function EndApplicationDialog({ application, onClose, today }: EndApplicationDialogProps) {
  return (
    <Dialog open={application !== null} onOpenChange={(open) => (open ? undefined : onClose())}>
      {application ? (
        <EndApplicationContent
          key={application.id}
          application={application}
          onClose={onClose}
          today={today}
        />
      ) : null}
    </Dialog>
  );
}

function EndApplicationContent({
  application,
  onClose,
  today,
}: {
  application: WorkerSchedulePattern;
  onClose: () => void;
  today: string;
}) {
  const { toast } = useToast();
  const { readOnly } = useReadOnly();
  const end = useEndPatternApplication();
  const [endDay, setEndDay] = useState(() => defaultEndDay(application, today));
  const [error, setError] = useState<string | undefined>(undefined);

  function submit() {
    const parsed = EndPatternApplicationBodySchema.safeParse({ end_day: endDay });
    if (!parsed.success) {
      setError(t('schedules.patterns.end.errors.endDay'));
      return;
    }
    if (parsed.data.end_day < application.start_day) {
      setError(
        t('schedules.patterns.end.errors.beforeStart', {
          day: formatLocalDay(application.start_day),
        }),
      );
      return;
    }
    setError(undefined);
    end.mutate(
      { id: application.id, end_day: parsed.data.end_day },
      {
        onSuccess: (saved) => {
          toast({
            title: t('schedules.patterns.toast.ended', {
              pattern: saved.pattern.name,
              worker: saved.worker.name,
            }),
            variant: 'success',
          });
          onClose();
        },
        onError: (apiError) => {
          const issues = apiError.details?.['issues'];
          const first = Array.isArray(issues)
            ? (issues as { message?: unknown }[]).find((issue) => typeof issue.message === 'string')
            : undefined;
          setError(
            apiError.is('VALIDATION_ERROR') && typeof first?.message === 'string'
              ? first.message
              : isStatusConflict(apiError)
                ? t('schedules.patterns.errors.ended')
                : scheduleErrorMessage(apiError),
          );
        },
      },
    );
  }

  return (
    <DialogContent
      title={t('schedules.patterns.end.title')}
      description={t('schedules.patterns.end.description', {
        pattern: application.pattern.name,
        worker: application.worker.name,
        start: formatLocalDay(application.start_day),
      })}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={end.isPending}>
            {t('common.buttons.back')}
          </Button>
          <Button
            variant="destructive"
            type="submit"
            form="pattern-end-form"
            loading={end.isPending}
            loadingText={t('common.states.saving')}
            {...readOnlyProps(readOnly)}
          >
            {t('schedules.patterns.end.confirm')}
          </Button>
        </>
      }
    >
      <form
        id="pattern-end-form"
        data-testid="pattern-end-form"
        className="flex flex-col gap-fm-3"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <p className="text-fm-sm text-fm-text-secondary">{t('schedules.patterns.end.notice')}</p>
        <Field label={t('schedules.patterns.end.endDay')} required error={error}>
          {(control) => (
            <Input
              {...control}
              type="date"
              min={application.start_day}
              value={endDay}
              onChange={(event) => setEndDay(event.target.value)}
            />
          )}
        </Field>
      </form>
    </DialogContent>
  );
}
